import moment, { Moment } from 'moment';
import momentTz from 'moment-timezone';
import { formatNumber } from '@angular/common';
import { formatNumberWithLeadingSign } from './leading-sign';
import { isNumber } from '../type-checkers';

export function getEmptyToDashesValueMapper(dashesNumber = 1) {
  return function (value: any) {
    if (
      isEmptyPrimitive(value) ||
      (value instanceof Array && value.length === 0)
    ) {
      return '-'.repeat(dashesNumber);
    }
    return value;
  };
}

export function getJoinWithSeparatorValueMapper(separator = ',') {
  return function (value: any) {
    if (Array.isArray(value)) {
      return value.join(`${separator} `) || '-';
    }
    return value || '-';
  };
}

export function removeDuplicationsValueMapper(value: any) {
  if (Array.isArray(value)) {
    return Array.from(new Set(value));
  }
  return value;
}

export function takeFirstValue(value: any) {
  if (Array.isArray(value)) {
    return value[0];
  }

  return value;
}

export function takeInParentheses(value: any) {
  if (Array.isArray(value) || isEmptyPrimitive(value)) {
    return value;
  }
  return `(${value})`;
}

export function getVariousValueMapper(varReplacer = 'Various') {
  return function (value: any) {
    if (Array.isArray(value)) {
      return value.some((val) => val !== value[0]) ? varReplacer : value[0];
    }
    return value;
  };
}

export function formatWithTz(date: Moment, formatString: string): string {
  return momentTz(date.toDate()).tz('America/New_York').format(formatString);
}

export function getMomentsFormatter(
  formatString = 'MM/DD/YYYY',
  withTz = false
) {
  function singleValueToFormat(value: any) {
    if (moment.isMoment(value)) {
      return withTz
        ? formatWithTz(value, formatString)
        : value.format(formatString);
    }
    return value;
  }

  return function (value: any) {
    if (Array.isArray(value)) {
      return value.map(singleValueToFormat);
    }

    return singleValueToFormat(value);
  };
}

export function filterEmptyValues(value: any) {
  if (Array.isArray(value)) {
    return value.filter((v) => v !== null && v !== undefined);
  }
  return value;
}

export function getPriceAsStringMapper(locale: string) {
  return function (value: any) {
    if (isNumber(value)) {
      return formatNumber(value, locale, '1.0-0');
    }
    return value;
  };
}

export function getPrepandNumbersWithSignMapper(locale: string) {
  return function (value: any) {
    if (Array.isArray(value)) {
      return value.map((val) =>
        formatNumberWithLeadingSign(val, locale, {
          int: '1.0-0',
          float: '1.0-0'
        })
      );
    }
    if (isNumber(value)) {
      return formatNumberWithLeadingSign(value, locale, {
        int: '1.0-0',
        float: '1.0-0'
      });
    }
    return value;
  };
}

export function getBooleanAsStringMapper() {
  return function (value: any) {
    return isEmptyPrimitive(value) ? value : value ? 'Yes' : 'No';
  };
}

export function getNumberAsStringMapper(precision: number) {
  return function (value: any) {
    if (typeof value !== 'number') return value;
    return value.toFixed(precision);
  };
}

export function getFiledFromObject(field: string) {
  return function (value) {
    return value ? value[field] : value;
  };
}

export function useFieldMappers<T>(
  fieldName: keyof T,
  mappers: ((value: any) => any)[]
) {
  return function (value) {
    if (
      typeof value === 'object' &&
      value !== null &&
      Object.prototype.hasOwnProperty.call(value, fieldName)
    ) {
      return {
        ...value,
        [fieldName]: mappers.reduce(
          (prevValue, mapper) => mapper(prevValue),
          value[fieldName]
        )
      };
    }
    return value;
  };
}

function isEmptyPrimitive(value: any): boolean {
  return value === null || value === undefined || value === '';
}

export function getCorrectSeniorManager(seniorManager) {
  return seniorManager && !seniorManager.name ? null : seniorManager;
}

export function getInlineArray(
  array: unknown[],
  mapper = (item) => item
): string {
  return array
    .map(mapper)
    .filter((v, i, a) => a.indexOf(v) === i)
    .join(', ');
}
